/**
 * Human Layer — My Profile fixture.
 *
 * ILLUSTRATIVE, and minimal on purpose — the same discipline `fixtures/access.ts`
 * uses for `SESSION_PROFILE`. The only person here is the demo account the rest
 * of the fixtures already use: `displayName` and `role` match `SESSION_USER`
 * (fixtures/catalogue.ts) because that is an already-known fact, not a new one
 * invented for this fixture. Fields this repo has no source for — `territory`,
 * `language` — stay genuinely empty rather than invented, for the same reason
 * `SESSION_PROFILE.nationality`/`.language` are empty. Nothing here mutates:
 * `getProfile` always returns this seed, and `updateProfile` (staticAdapter.ts)
 * builds a fresh object over it instead of writing back to it, so a page reload
 * restores the seed exactly (see staticAdapter.ts's file header).
 */

import type { HumanLayerProfile } from '../../contracts';

/**
 * This repo has no user-id primitive yet — `SessionUser` carries a display
 * name and role but never an id, because there has only ever been one demo
 * session to describe. `owner.userId`, `createdBy` and `updatedBy` need a
 * stable string regardless, so this is one opaque, obviously-synthetic id
 * rather than a GUID or employee number that would look sourced from an
 * identity system that does not exist here.
 */
export const DEMO_USER_ID = 'session-user';

/** Fixed, because nothing here persists — mirrors `VC_RATE_CARD_REVISION.version`. */
export const HUMAN_LAYER_PROFILE_VERSION = 'fixture-1';

/**
 * The pre-save state every demo session starts from. `status: 'not_configured'`
 * even though `displayName`/`role` are already filled in: knowing who somebody
 * is is not the same as them having opened My Profile and confirmed how they
 * want agents to write for them. `workingPreferences` starts at the safest
 * platform defaults (review everything, standard depth, no extra notifications)
 * rather than a guess at what this person would choose.
 */
export const HUMAN_LAYER_PROFILE_SEED: HumanLayerProfile = {
  id: 'human-layer-profile-session-user',
  owner: { scope: 'individual', userId: DEMO_USER_ID },
  identity: {
    displayName: 'Paulo Pinto',
    role: 'Product Owner · Partnership Net',
    territory: [],
    language: '',
  },
  workingPreferences: {
    reviewCadence: 'review_every_output',
    preferredOutputDepth: 'standard',
    notifyOn: [],
  },
  status: 'not_configured',
  audit: {
    createdBy: DEMO_USER_ID,
    createdAt: '2026-01-05',
    updatedBy: DEMO_USER_ID,
    updatedAt: '2026-01-05',
    version: HUMAN_LAYER_PROFILE_VERSION,
  },
};
